import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Crosshair } from "lucide-react";
import Header from "@/components/Header";
import { ModuleMapUiStubProvider } from "@/components/module-map/ModuleMapUiStubProvider";
import {
  clampVisualizerLayersWidth,
  VISUALIZER_LAYERS_WIDTH_DEFAULT,
  VisualizerLayersPanel,
  type LibraryPick,
} from "@/components/visualizer/VisualizerLayersPanel";
import { VisualizerLegendToggle } from "@/components/visualizer/VisualizerLegendToggle";
import {
  VisualizerToolbar,
  VISUALIZER_TOOL_HOTKEYS,
  type VisualizerToolMode,
} from "@/components/visualizer/VisualizerToolbar";
import { VisualizerMapCompass } from "@/components/visualizer/VisualizerMapCompass";
import { MapLibreMeasureTool } from "@/components/map-libre/MapLibreMeasureTool";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { Button } from "@/components/ui/button";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuthV2";
import { MAP_PANEL_Z } from "@/lib/map-panel-z";
import { signsInsideLasso } from "@/lib/visualizer-cad-geometry";
import {
  asSignSnapshot,
  featureToCreatePayload,
  featureToStylePatch,
  snapshotForCopy,
  VisualizerSignHistory,
  type VisualizerSignSnapshot,
} from "@/lib/visualizer-sign-history";
import {
  applyVisualizerSnap,
  buildVisualizerGridFeatureCollection,
  collectSignVertices,
  loadVisualizerSnapGridPrefs,
  saveVisualizerSnapGridPrefs,
  VISUALIZER_GRID_LAYER,
  VISUALIZER_GRID_SOURCE,
  type VisualizerGridStepMeters,
  type VisualizerSnapGridPrefs,
} from "@/lib/visualizer-snap-grid";
import { cn } from "@/lib/utils";
import { objectsApi } from "@/services/api-v2.service";
import {
  visualizerApi,
  type VisualizerSignFeature,
} from "@/services/visualizer.service";
import type { Map as MapLibreMapInstance } from "maplibre-gl";

const MapLibreMap = lazy(() => import("@/components/map-libre/MapLibreMap"));

export default function VisualizerWorkspacePage() {
  const { toast } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("visualizer.manage");
  const queryClient = useQueryClient();
  const [sceneId, setSceneId] = useState<number | null>(null);
  const [sceneName, setSceneName] = useState("");
  const [pick, setPick] = useState<LibraryPick | null>(null);
  const [toolMode, setToolMode] = useState<VisualizerToolMode>("select");
  const [draft, setDraft] = useState<[number, number][]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const selectedId = selectedIds[0] ?? null;
  const setSelectedId = (
    next: number | null | ((prev: number | null) => number | null),
  ) => {
    setSelectedIds((ids) => {
      const prev = ids[0] ?? null;
      const value = typeof next === "function" ? next(prev) : next;
      return value == null ? [] : [value];
    });
  };
  const [legendOpen, setLegendOpen] = useState(false);
  const [layersCollapsed, setLayersCollapsed] = useState(() => {
    try {
      return sessionStorage.getItem("gpml.visualizer.layers.collapsed") === "1";
    } catch {
      return false;
    }
  });
  const setLayersCollapsedPersist = (collapsed: boolean) => {
    setLayersCollapsed(collapsed);
    try {
      sessionStorage.setItem("gpml.visualizer.layers.collapsed", collapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
  };
  const [layersWidthPx, setLayersWidthPx] = useState(() => {
    try {
      const raw = Number(sessionStorage.getItem("gpml.visualizer.layers.width"));
      if (Number.isFinite(raw)) return clampVisualizerLayersWidth(raw);
    } catch {
      /* ignore */
    }
    return VISUALIZER_LAYERS_WIDTH_DEFAULT;
  });
  const setLayersWidthPersist = (widthPx: number) => {
    const next = clampVisualizerLayersWidth(widthPx);
    setLayersWidthPx(next);
    try {
      sessionStorage.setItem("gpml.visualizer.layers.width", String(next));
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    const onResize = () => setLayersWidthPx((current) => clampVisualizerLayersWidth(current));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const [sizePx, setSizePx] = useState(32);
  const [rotation, setRotation] = useState(0);
  const [strokeColor, setStrokeColor] = useState("#2563eb");
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [strokeDash, setStrokeDash] = useState<"solid" | "dash" | "dot" | "dash_dot">("solid");
  const [fillColor, setFillColor] = useState("#2563eb");
  const [fillOpacity, setFillOpacity] = useState(0.2);
  const [label, setLabel] = useState("");
  const [selectedObjectId, setSelectedObjectId] = useState<number | null>(null);
  const [bindObjectFromMap, setBindObjectFromMap] = useState(false);
  const [bindObjectBusy, setBindObjectBusy] = useState(false);
  const [textDraft, setTextDraft] = useState<{ lng: number; lat: number } | null>(null);
  const [textDraftLabel, setTextDraftLabel] = useState("");
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const toolModeRef = useRef(toolMode);
  toolModeRef.current = toolMode;
  const bindObjectFromMapRef = useRef(bindObjectFromMap);
  bindObjectFromMapRef.current = bindObjectFromMap;
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const canManageRef = useRef(canManage);
  canManageRef.current = canManage;
  const pickRef = useRef(pick);
  pickRef.current = pick;
  const setModeRef = useRef<(mode: VisualizerToolMode) => void>(() => {});
  const historyRef = useRef(new VisualizerSignHistory());
  const historyReplayingRef = useRef(false);
  const [historyTick, setHistoryTick] = useState(0);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [mapInstance, setMapInstance] = useState<MapLibreMapInstance | null>(null);
  const [snapGridPrefs, setSnapGridPrefs] = useState<VisualizerSnapGridPrefs>(() =>
    loadVisualizerSnapGridPrefs(),
  );
  const setSnapGridPrefsPersist = useCallback((next: VisualizerSnapGridPrefs) => {
    setSnapGridPrefs(next);
    saveVisualizerSnapGridPrefs(next);
  }, []);
  const snapGridPrefsRef = useRef(snapGridPrefs);
  snapGridPrefsRef.current = snapGridPrefs;

  const bumpHistory = () => setHistoryTick((n) => n + 1);
  const canUndo = historyTick >= 0 && historyRef.current.canUndo;
  const canRedo = historyTick >= 0 && historyRef.current.canRedo;

  const panMode = toolMode === "pan";
  const measureMode = toolMode === "measure";
  const placeMode = toolMode === "point";
  const textMode = toolMode === "text";
  const freehandMode = toolMode === "freehand";
  const eraserMode = toolMode === "eraser";
  const lassoMode = toolMode === "lasso";
  const rotateMode = toolMode === "rotate";
  const vertexMode = toolMode === "vertex";
  const scissorsMode = toolMode === "scissors";
  const shapeMode =
    toolMode === "line" || toolMode === "polygon"
      ? toolMode
      : scissorsMode
        ? "line"
        : null;
  const measureToolRef = useRef<MapLibreMeasureTool | null>(null);

  useEffect(() => {
    const map = mapInstance;
    if (!map || !measureMode) {
      measureToolRef.current?.deactivate();
      return;
    }
    measureToolRef.current?.deactivate();
    measureToolRef.current = new MapLibreMeasureTool(map);
    measureToolRef.current.activate();
    return () => {
      measureToolRef.current?.deactivate();
    };
  }, [measureMode, mapInstance]);

  useEffect(() => {
    return () => {
      measureToolRef.current?.deactivate();
      measureToolRef.current = null;
    };
  }, []);

  const scenesQuery = useQuery({
    queryKey: ["visualizer", "scenes"],
    queryFn: () => visualizerApi.listScenes(),
  });
  const signsQuery = useQuery({
    queryKey: ["visualizer", "signs", sceneId],
    queryFn: () => visualizerApi.signs(sceneId as number),
    enabled: sceneId != null,
  });
  const layerGroupsQuery = useQuery({
    queryKey: ["visualizer", "layer-groups", sceneId],
    queryFn: () => visualizerApi.listLayerGroups(sceneId as number),
    enabled: sceneId != null,
  });
  const legendQuery = useQuery({
    queryKey: ["visualizer", "legend", sceneId],
    queryFn: () => visualizerApi.legend(sceneId as number),
    enabled: sceneId != null,
  });
  const libraryQuery = useQuery({
    queryKey: ["visualizer", "library"],
    queryFn: () => visualizerApi.library(),
  });

  const signs = useMemo(
    () => (signsQuery.data?.features ?? []) as VisualizerSignFeature[],
    [signsQuery.data],
  );
  const mapSignsGeoJson = useMemo((): GeoJSON.FeatureCollection => {
    if (sceneId == null) {
      return { type: "FeatureCollection", features: [] };
    }
    const features = signs.filter(
      (feature) => feature.properties?.effectivelyVisible !== false,
    );
    return { type: "FeatureCollection", features };
  }, [sceneId, signs]);
  const signsRef = useRef(signs);
  signsRef.current = signs;
  const mapInstanceRef = useRef(mapInstance);
  mapInstanceRef.current = mapInstance;

  const snapLngLat = useCallback(
    (lng: number, lat: number): { lng: number; lat: number } => {
      const prefs = snapGridPrefsRef.current;
      const map = mapInstanceRef.current;
      if (!prefs.snap || !canManageRef.current || !map) {
        return { lng, lat };
      }
      const result = applyVisualizerSnap(map, lng, lat, {
        snapEnabled: true,
        gridEnabled: prefs.grid,
        stepMeters: prefs.stepMeters,
        signVertices: collectSignVertices(signsRef.current),
      });
      return { lng: result.lng, lat: result.lat };
    },
    [],
  );

  useEffect(() => {
    const map = mapInstance;
    if (!map) return;

    const empty: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };

    const ensureLayer = () => {
      if (!map.isStyleLoaded()) return;
      if (!map.getSource(VISUALIZER_GRID_SOURCE)) {
        map.addSource(VISUALIZER_GRID_SOURCE, { type: "geojson", data: empty });
      }
      if (!map.getLayer(VISUALIZER_GRID_LAYER)) {
        const beforeId = map.getLayer("visualizer-signs-halo")
          ? "visualizer-signs-halo"
          : map.getLayer("visualizer-signs-fill")
            ? "visualizer-signs-fill"
            : undefined;
        map.addLayer(
          {
            id: VISUALIZER_GRID_LAYER,
            type: "line",
            source: VISUALIZER_GRID_SOURCE,
            layout: { "line-cap": "butt", "line-join": "miter" },
            paint: {
              "line-color": "#64748b",
              "line-width": 1,
              "line-opacity": 0.35,
            },
          },
          beforeId,
        );
      }
    };

    const refresh = () => {
      if (!map.isStyleLoaded()) return;
      ensureLayer();
      const source = map.getSource(VISUALIZER_GRID_SOURCE) as
        | { setData: (data: GeoJSON.FeatureCollection) => void }
        | undefined;
      if (!source) return;
      if (!snapGridPrefs.grid) {
        source.setData(empty);
        return;
      }
      source.setData(buildVisualizerGridFeatureCollection(map.getBounds(), snapGridPrefs.stepMeters));
    };

    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(refresh, 80);
    };

    const onReady = () => {
      refresh();
    };
    if (map.isStyleLoaded()) onReady();
    map.on("load", onReady);
    map.on("style.load", onReady);

    map.on("moveend", schedule);
    map.on("zoomend", schedule);
    map.on("styledata", schedule);

    return () => {
      if (timer) clearTimeout(timer);
      map.off("moveend", schedule);
      map.off("zoomend", schedule);
      map.off("styledata", schedule);
      map.off("load", onReady);
      map.off("style.load", onReady);
      try {
        if (map.getLayer(VISUALIZER_GRID_LAYER)) map.removeLayer(VISUALIZER_GRID_LAYER);
        if (map.getSource(VISUALIZER_GRID_SOURCE)) map.removeSource(VISUALIZER_GRID_SOURCE);
      } catch {
        /* style may be gone */
      }
    };
  }, [mapInstance, snapGridPrefs.grid, snapGridPrefs.stepMeters]);

  const layerGroups = layerGroupsQuery.data ?? [];

  const selected = useMemo(
    () => signs.find((feature) => Number(feature.properties?.id) === selectedId) ?? null,
    [signs, selectedId],
  );
  const selectedIsText = selected?.properties?.kind === "text";

  useEffect(() => {
    if (!selected) return;
    setSizePx(Number(selected.properties.sizePx) || 32);
    setRotation(Number(selected.properties.rotationDeg) || 0);
    setStrokeColor(selected.properties.strokeColor || "#2563eb");
    setStrokeWidth(Number(selected.properties.strokeWidth) || 2);
    setStrokeDash(
      selected.properties.strokeDash === "dash" ||
        selected.properties.strokeDash === "dot" ||
        selected.properties.strokeDash === "dash_dot"
        ? selected.properties.strokeDash
        : "solid",
    );
    setFillColor(
      selected.properties.fillColor || selected.properties.strokeColor || "#2563eb",
    );
    setFillOpacity(Number(selected.properties.fillOpacity ?? 0.2));
    setLabel(selected.properties.label || "");
    setSelectedObjectId(
      selected.properties.objectId != null && Number.isFinite(Number(selected.properties.objectId))
        ? Number(selected.properties.objectId)
        : null,
    );
  }, [selected]);

  const finishDraftRef = useRef<() => void>(() => {});
  const splitSignRef = useRef<(coordinates: number[][]) => void>(() => {});
  const sceneIdRef = useRef(sceneId);
  sceneIdRef.current = sceneId;

  useEffect(() => {
    historyRef.current.clear();
    bumpHistory();
  }, [sceneId]);

  const setMode = (mode: VisualizerToolMode) => {
    setToolMode(mode);
    setDraft([]);
    setTextDraft(null);
    setTextDraftLabel("");
    setBindObjectFromMap(false);
    // Выбор / CAD-режимы сохраняют выделение; постановка и пан снимают.
    if (mode === "pan" || mode === "point" || mode === "line" || mode === "polygon" || mode === "text" || mode === "freehand") {
      setSelectedIds([]);
    }
  };
  setModeRef.current = setMode;

  useEffect(() => {
    if (selectedId == null) setBindObjectFromMap(false);
  }, [selectedId]);

  const invalidateScene = async (id: number) => {
    await queryClient.invalidateQueries({ queryKey: ["visualizer", "signs", id] });
    await queryClient.invalidateQueries({ queryKey: ["visualizer", "legend", id] });
    await queryClient.invalidateQueries({ queryKey: ["visualizer", "layer-groups", id] });
  };

  const isSignEffectivelyLocked = (id: number | null) => {
    if (id == null) return false;
    const feature = signs.find((f) => Number(f.properties?.id) === id);
    return Boolean(feature?.properties?.effectivelyLocked);
  };
  const canMergeSelection =
    selectedIds.length >= 2 &&
    selectedIds.every((id) => {
      const f = signs.find((x) => Number(x.properties?.id) === id);
      return f?.geometry.type === "Polygon" && !isSignEffectivelyLocked(id);
    });
  const canVertexEditSelection =
    selectedIds.length === 1 &&
    selectedId != null &&
    !isSignEffectivelyLocked(selectedId) &&
    (() => {
      const f = signs.find((x) => Number(x.properties?.id) === selectedId);
      return f?.geometry.type === "LineString" || f?.geometry.type === "Polygon";
    })();
  const canScissorsSelection = canVertexEditSelection;
  const canDifferenceSelection =
    selectedIds.length === 2 &&
    selectedIds.every((id) => {
      const f = signs.find((x) => Number(x.properties?.id) === id);
      return f?.geometry.type === "Polygon" && !isSignEffectivelyLocked(id);
    });

  useEffect(() => {
    if (toolMode === "vertex" && !canVertexEditSelection) {
      setToolMode("select");
    }
  }, [toolMode, canVertexEditSelection]);

  useEffect(() => {
    if (toolMode === "scissors" && !canScissorsSelection) {
      setToolMode("select");
      setDraft([]);
    }
  }, [toolMode, canScissorsSelection]);

  const pushCreateHistory = (created: VisualizerSignFeature) => {
    if (historyReplayingRef.current) return;
    const snap = asSignSnapshot(created);
    if (!snap) return;
    const logicalId = snap.properties.id;
    const history = historyRef.current;
    history.push({
      undo: async () => {
        const sid = sceneIdRef.current;
        if (sid == null) return;
        const id = history.resolveId(logicalId);
        await visualizerApi.deleteSign(sid, id);
        await invalidateScene(sid);
        setSelectedId((cur) => (cur === id ? null : cur));
      },
      redo: async () => {
        const sid = sceneIdRef.current;
        if (sid == null) return;
        const createdAgain = (await visualizerApi.createSign(
          sid,
          featureToCreatePayload(snap),
        )) as VisualizerSignFeature;
        const newSnap = asSignSnapshot(createdAgain);
        if (newSnap) history.remapId(logicalId, newSnap.properties.id);
        await invalidateScene(sid);
      },
    });
    bumpHistory();
  };

  const pushDeleteHistory = (before: VisualizerSignSnapshot) => {
    if (historyReplayingRef.current) return;
    const logicalId = before.properties.id;
    const history = historyRef.current;
    history.push({
      undo: async () => {
        const sid = sceneIdRef.current;
        if (sid == null) return;
        const created = (await visualizerApi.createSign(
          sid,
          featureToCreatePayload(before),
        )) as VisualizerSignFeature;
        const newSnap = asSignSnapshot(created);
        if (newSnap) history.remapId(logicalId, newSnap.properties.id);
        await invalidateScene(sid);
      },
      redo: async () => {
        const sid = sceneIdRef.current;
        if (sid == null) return;
        const id = history.resolveId(logicalId);
        await visualizerApi.deleteSign(sid, id);
        await invalidateScene(sid);
        setSelectedId((cur) => (cur === id ? null : cur));
      },
    });
    bumpHistory();
  };

  const pushPatchHistory = (
    logicalId: number,
    beforePatch: Record<string, unknown>,
    afterPatch: Record<string, unknown>,
  ) => {
    if (historyReplayingRef.current) return;
    const history = historyRef.current;
    history.push({
      undo: async () => {
        const sid = sceneIdRef.current;
        if (sid == null) return;
        await visualizerApi.updateSign(sid, history.resolveId(logicalId), beforePatch);
        await invalidateScene(sid);
      },
      redo: async () => {
        const sid = sceneIdRef.current;
        if (sid == null) return;
        await visualizerApi.updateSign(sid, history.resolveId(logicalId), afterPatch);
        await invalidateScene(sid);
      },
    });
    bumpHistory();
  };

  const copySignRef = useRef<() => void>(() => {});
  const runHistoryRef = useRef<(action: "undo" | "redo") => Promise<void>>(async () => {});
  runHistoryRef.current = async (action: "undo" | "redo") => {
    if (!canManage || historyBusy || historyReplayingRef.current) return;
    if (action === "undo" && !historyRef.current.canUndo) return;
    if (action === "redo" && !historyRef.current.canRedo) return;
    setHistoryBusy(true);
    historyReplayingRef.current = true;
    try {
      const ok =
        action === "undo" ? await historyRef.current.undo() : await historyRef.current.redo();
      if (!ok) return;
      bumpHistory();
    } catch (error) {
      toast({
        title: action === "undo" ? "Не удалось отменить" : "Не удалось повторить",
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      historyReplayingRef.current = false;
      setHistoryBusy(false);
      bumpHistory();
    }
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      const typing =
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        Boolean(target?.isContentEditable);
      if (event.key === "Escape") {
        if (bindObjectFromMapRef.current) {
          setBindObjectFromMap(false);
          return;
        }
        setToolMode("select");
        setDraft([]);
        setTextDraft(null);
        setTextDraftLabel("");
        setSelectedId(null);
        return;
      }
      if (typing) return;
      const mod = event.ctrlKey || event.metaKey;
      if (mod && event.key.toLowerCase() === "z" && !event.shiftKey) {
        event.preventDefault();
        void runHistoryRef.current("undo");
        return;
      }
      if (
        mod &&
        (event.key.toLowerCase() === "y" || (event.key.toLowerCase() === "z" && event.shiftKey))
      ) {
        event.preventDefault();
        void runHistoryRef.current("redo");
        return;
      }
      if (mod && event.key.toLowerCase() === "d") {
        event.preventDefault();
        copySignRef.current();
        return;
      }
      if (!mod && !event.altKey) {
        const key = event.key.length === 1 ? event.key.toUpperCase() : event.key;
        const hotkeyMode = (
          Object.entries(VISUALIZER_TOOL_HOTKEYS) as [VisualizerToolMode, string][]
        ).find(([, hk]) => hk === key)?.[0];
        if (hotkeyMode) {
          const manage = canManageRef.current;
          const hasScene = sceneIdRef.current != null;
          const hasPick = pickRef.current != null;
          const libraryDraw =
            hotkeyMode === "point" ||
            hotkeyMode === "line" ||
            hotkeyMode === "polygon" ||
            hotkeyMode === "freehand";
          if (hotkeyMode === "measure" && !hasScene) return;
          if (hotkeyMode === "text" && (!manage || !hasScene)) return;
          if (libraryDraw && (!manage || !hasScene || !hasPick)) return;
          event.preventDefault();
          setModeRef.current(hotkeyMode);
          return;
        }
      }
      const drawing =
        toolModeRef.current === "line" ||
        toolModeRef.current === "polygon" ||
        toolModeRef.current === "scissors";
      if (event.key === "Backspace" && drawing) {
        event.preventDefault();
        setDraft((points) => points.slice(0, -1));
      }
      if (event.key === "Enter" && drawing) {
        event.preventDefault();
        finishDraftRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const createScene = useMutation({
    mutationFn: () => visualizerApi.createScene(sceneName),
    onSuccess: async (scene) => {
      setSceneName("");
      setSceneId(scene.id);
      await queryClient.invalidateQueries({ queryKey: ["visualizer", "scenes"] });
    },
    onError: (error: Error) => toast({ title: "Сцена не создана", description: error.message }),
  });

  const removeScene = useMutation({
    mutationFn: (id: number) => visualizerApi.deleteScene(id),
    onSuccess: async () => {
      setSceneId(null);
      setSelectedId(null);
      await queryClient.invalidateQueries({ queryKey: ["visualizer", "scenes"] });
    },
  });

  const exportScene = useMutation({
    mutationFn: async () => {
      if (sceneId == null) throw new Error("Выберите сцену");
      const packageData = await visualizerApi.exportScene(sceneId);
      const blob = new Blob([JSON.stringify(packageData, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeName = (packageData.scene.name || "scene").replace(/[^\wа-яА-ЯёЁ\-]+/g, "_");
      link.href = url;
      link.download = `${safeName}.visualizer.json`;
      link.click();
      URL.revokeObjectURL(url);
      return packageData;
    },
    onSuccess: () => toast({ title: "Сцена экспортирована" }),
    onError: (error: Error) =>
      toast({ title: "Экспорт не удался", description: error.message, variant: "destructive" }),
  });

  const importScene = useMutation({
    mutationFn: async (file: File) => {
      const text = await file.text();
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error("Файл не является JSON");
      }
      return visualizerApi.importScene(parsed as Parameters<typeof visualizerApi.importScene>[0]);
    },
    onSuccess: async (scene) => {
      setSceneId(scene.id);
      setSelectedId(null);
      setMode("select");
      await queryClient.invalidateQueries({ queryKey: ["visualizer", "scenes"] });
      await invalidateScene(scene.id);
      toast({ title: "Сцена импортирована", description: scene.name });
    },
    onError: (error: Error) =>
      toast({ title: "Импорт не удался", description: error.message, variant: "destructive" }),
  });

  const duplicateScene = useMutation({
    mutationFn: async () => {
      if (sceneId == null) throw new Error("Выберите сцену");
      return visualizerApi.duplicateScene(sceneId);
    },
    onSuccess: async (scene) => {
      setSceneId(scene.id);
      setSelectedId(null);
      setMode("select");
      await queryClient.invalidateQueries({ queryKey: ["visualizer", "scenes"] });
      await invalidateScene(scene.id);
      toast({ title: "Сцена продублирована", description: scene.name });
    },
    onError: (error: Error) =>
      toast({ title: "Дублирование не удалось", description: error.message, variant: "destructive" }),
  });

  const placeSign = useMutation({
    mutationFn: (point: { lng: number; lat: number }) => {
      if (sceneId == null || !pick) throw new Error("Выберите сцену и знак");
      return visualizerApi.createSign(sceneId, {
        lng: point.lng,
        lat: point.lat,
        source: pick.source,
        kls_id: pick.source === "kls" ? pick.klsId : null,
        symbol_asset_id: pick.source === "asset" ? pick.assetId : null,
      });
    },
    onSuccess: async (created) => {
      pushCreateHistory(created as VisualizerSignFeature);
      if (sceneId != null) await invalidateScene(sceneId);
    },
    onError: (error: Error) => toast({ title: "Знак не поставлен", description: error.message }),
  });

  const placeText = useMutation({
    mutationFn: (body: { lng: number; lat: number; label: string }) => {
      if (sceneId == null) throw new Error("Выберите сцену");
      return visualizerApi.createSign(sceneId, {
        kind: "text",
        lng: body.lng,
        lat: body.lat,
        label: body.label,
        size_px: 16,
        stroke_color: "#111827",
      });
    },
    onSuccess: async (created) => {
      pushCreateHistory(created as VisualizerSignFeature);
      setTextDraft(null);
      setTextDraftLabel("");
      setMode("select");
      if (sceneId != null) await invalidateScene(sceneId);
    },
    onError: (error: Error) => toast({ title: "Текст не сохранён", description: error.message }),
  });

  const placeShape = useMutation({
    mutationFn: (body: { kind: "line" | "polygon"; coordinates: [number, number][] }) => {
      if (sceneId == null || !pick) throw new Error("Выберите сцену и знак");
      return visualizerApi.createSign(sceneId, {
        kind: body.kind,
        coordinates: body.coordinates,
        source: pick.source,
        kls_id: pick.source === "kls" ? pick.klsId : null,
        symbol_asset_id: pick.source === "asset" ? pick.assetId : null,
      });
    },
    onSuccess: async (created) => {
      pushCreateHistory(created as VisualizerSignFeature);
      setDraft([]);
      if (sceneId != null) await invalidateScene(sceneId);
    },
    onError: (error: Error) => toast({ title: "Знак не сохранён", description: error.message }),
  });

  finishDraftRef.current = () => {
    const mode = toolModeRef.current;
    let points = draftRef.current;
    if (mode !== "line" && mode !== "polygon" && mode !== "scissors") return;
    if (points.length >= 2) {
      const last = points[points.length - 1];
      const prev = points[points.length - 2];
      if (last[0] === prev[0] && last[1] === prev[1]) points = points.slice(0, -1);
    }
    if (mode === "scissors") {
      if (points.length < 2) {
        toast({ title: "Режущей линии нужны две точки" });
        return;
      }
      splitSignRef.current(points);
      return;
    }
    const min = mode === "line" ? 2 : 3;
    if (points.length < min) {
      toast({
        title: mode === "line" ? "Линии нужны две точки" : "Площади нужны три точки",
      });
      return;
    }
    placeShape.mutate({ kind: mode, coordinates: points });
  };

  const patchSign = useMutation({
    mutationFn: (data: Record<string, unknown>) => {
      if (sceneId == null || selectedId == null) throw new Error("Знак не выбран");
      const unlockOnly =
        Object.keys(data).length === 1 && Object.prototype.hasOwnProperty.call(data, "is_locked");
      if (isSignEffectivelyLocked(selectedId) && !unlockOnly) {
        throw new Error("Знак заблокирован");
      }
      const feature = signs.find((f) => Number(f.properties?.id) === selectedId);
      const beforeSnap = feature ? asSignSnapshot(feature) : null;
      const beforePatch = beforeSnap ? featureToStylePatch(beforeSnap) : null;
      return visualizerApi.updateSign(sceneId, selectedId, data).then((updated) => ({
        updated,
        signId: selectedId,
        beforePatch,
        afterPatch: data,
      }));
    },
    onSuccess: async (result) => {
      if (result.beforePatch) {
        pushPatchHistory(result.signId, result.beforePatch, result.afterPatch);
      }
      if (sceneId != null) await invalidateScene(sceneId);
    },
    onError: (error: Error) => toast({ title: "Не сохранено", description: error.message }),
  });

  const bindObjectFromMapClickRef = useRef<(objectId: number) => void>(() => {});
  bindObjectFromMapClickRef.current = (objectId: number) => {
    if (!canManage || !bindObjectFromMapRef.current || bindObjectBusy) return;
    const sid = sceneIdRef.current;
    const signId = selectedIdRef.current;
    if (sid == null || signId == null) return;
    const feature = signs.find((f) => Number(f.properties?.id) === signId);
    const beforeSnap = feature ? asSignSnapshot(feature) : null;
    const beforePatch = beforeSnap ? featureToStylePatch(beforeSnap) : null;
    const afterPatch = { object_id: objectId };
    setBindObjectBusy(true);
    void visualizerApi
      .updateSign(sid, signId, afterPatch)
      .then(async () => {
        if (beforePatch) pushPatchHistory(signId, beforePatch, afterPatch);
        setSelectedObjectId(objectId);
        setBindObjectFromMap(false);
        await invalidateScene(sid);
        toast({ title: "Объект привязан с карты" });
      })
      .catch((error: unknown) => {
        toast({
          title: "Не удалось привязать",
          description: error instanceof Error ? error.message : undefined,
          variant: "destructive",
        });
      })
      .finally(() => setBindObjectBusy(false));
  };

  const removeSign = useMutation({
    mutationFn: (id: number) => {
      if (sceneId == null) throw new Error("Сцена не выбрана");
      if (isSignEffectivelyLocked(id)) throw new Error("Знак заблокирован");
      const feature = signs.find((f) => Number(f.properties?.id) === id);
      const before = feature ? asSignSnapshot(feature) : null;
      return visualizerApi.deleteSign(sceneId, id).then(() => ({ id, before }));
    },
    onSuccess: async (result) => {
      if (result.before) pushDeleteHistory(result.before);
      setSelectedId(null);
      if (sceneId != null) await invalidateScene(sceneId);
    },
    onError: (error: Error) =>
      toast({ title: "Удаление", description: error.message, variant: "destructive" }),
  });

  const createLayerGroup = useMutation({
    mutationFn: (name: string) => {
      if (sceneId == null) throw new Error("Сцена не выбрана");
      return visualizerApi.createLayerGroup(sceneId, { name });
    },
    onSuccess: async () => {
      if (sceneId != null) await invalidateScene(sceneId);
    },
    onError: (error: Error) =>
      toast({ title: "Группа", description: error.message, variant: "destructive" }),
  });

  const patchLayerGroup = useMutation({
    mutationFn: (body: {
      groupId: number;
      data: { name?: string; is_locked?: boolean; is_visible?: boolean };
    }) => {
      if (sceneId == null) throw new Error("Сцена не выбрана");
      return visualizerApi.updateLayerGroup(sceneId, body.groupId, body.data);
    },
    onSuccess: async () => {
      if (sceneId != null) await invalidateScene(sceneId);
    },
    onError: (error: Error) =>
      toast({ title: "Группа", description: error.message, variant: "destructive" }),
  });

  const deleteLayerGroup = useMutation({
    mutationFn: (groupId: number) => {
      if (sceneId == null) throw new Error("Сцена не выбрана");
      return visualizerApi.deleteLayerGroup(sceneId, groupId);
    },
    onSuccess: async () => {
      if (sceneId != null) await invalidateScene(sceneId);
    },
    onError: (error: Error) =>
      toast({ title: "Группа", description: error.message, variant: "destructive" }),
  });

  const patchSignMeta = async (
    signId: number,
    afterPatch: Record<string, unknown>,
  ) => {
    if (sceneId == null) return;
    const feature = signs.find((f) => Number(f.properties?.id) === signId);
    const beforeSnap = feature ? asSignSnapshot(feature) : null;
    const beforePatch = beforeSnap ? featureToStylePatch(beforeSnap) : null;
    await visualizerApi.updateSign(sceneId, signId, afterPatch);
    if (beforePatch) pushPatchHistory(signId, beforePatch, afterPatch);
    await invalidateScene(sceneId);
  };

  const copySign = useMutation({
    mutationFn: async () => {
      if (sceneId == null) throw new Error("Сцена не выбрана");
      const unlocked = selectedIds.filter((id) => !isSignEffectivelyLocked(id));
      const skipped = selectedIds.length - unlocked.length;
      if (unlocked.length === 0) {
        throw new Error("Нет незаблокированных знаков для копирования");
      }
      const createdFeatures: VisualizerSignFeature[] = [];
      const createdIds: number[] = [];
      for (const sourceId of unlocked) {
        const feature = signs.find((f) => Number(f.properties?.id) === sourceId);
        const snap = feature ? asSignSnapshot(feature) : null;
        if (!snap) throw new Error(`Не удалось прочитать знак ${sourceId}`);
        const shifted = snapshotForCopy(snap);
        const created = (await visualizerApi.createSign(
          sceneId,
          featureToCreatePayload(shifted),
        )) as VisualizerSignFeature;
        const createdSnap = asSignSnapshot(created);
        const newId = createdSnap?.properties.id;
        if (newId == null) throw new Error("Копия создана без id");
        try {
          const card = await visualizerApi.getSignCard(sceneId, sourceId);
          const values = card.values ?? {};
          if (Object.keys(values).length > 0) {
            await visualizerApi.putSignCard(sceneId, newId, values);
          }
        } catch {
          /* шаблон/карточка необязательны — геометрия уже скопирована */
        }
        createdFeatures.push(created);
        createdIds.push(newId);
      }
      return { createdFeatures, createdIds, copied: unlocked.length, skipped };
    },
    onSuccess: async (result) => {
      for (const created of result.createdFeatures) {
        pushCreateHistory(created);
      }
      setSelectedIds(result.createdIds);
      setMode("select");
      if (sceneId != null) await invalidateScene(sceneId);
      toast({
        title:
          result.copied === 1 ? "Знак скопирован" : `Скопировано знаков: ${result.copied}`,
        description:
          result.skipped > 0 ? `Пропущено (заблокированы): ${result.skipped}` : undefined,
      });
    },
    onError: (error: Error) =>
      toast({ title: "Копирование не удалось", description: error.message, variant: "destructive" }),
  });

  copySignRef.current = () => {
    if (!canManage || copySign.isPending) return;
    if (!selectedIds.some((id) => !isSignEffectivelyLocked(id))) return;
    copySign.mutate();
  };

  const mergeSigns = useMutation({
    mutationFn: async () => {
      if (sceneId == null) throw new Error("Сцена не выбрана");
      if (!canMergeSelection) throw new Error("Выберите минимум две незаблокированные площади");
      const beforeSnaps = selectedIds
        .map((id) => {
          const feature = signs.find((f) => Number(f.properties?.id) === id);
          return feature ? asSignSnapshot(feature) : null;
        })
        .filter((s): s is VisualizerSignSnapshot => s != null);
      const created = (await visualizerApi.mergeAreaSigns(
        sceneId,
        selectedIds,
      )) as VisualizerSignFeature;
      return { created, beforeSnaps };
    },
    onSuccess: async (result) => {
      if (!historyReplayingRef.current && result.beforeSnaps.length > 0) {
        const history = historyRef.current;
        const merges = result.beforeSnaps;
        const createdSnap = asSignSnapshot(result.created);
        const mergedLogicalId = createdSnap?.properties.id;
        history.push({
          undo: async () => {
            const sid = sceneIdRef.current;
            if (sid == null || mergedLogicalId == null) return;
            const liveId = history.resolveId(mergedLogicalId);
            await visualizerApi.deleteSign(sid, liveId);
            for (const snap of merges) {
              const again = (await visualizerApi.createSign(
                sid,
                featureToCreatePayload(snap),
              )) as VisualizerSignFeature;
              const againSnap = asSignSnapshot(again);
              if (againSnap) history.remapId(snap.properties.id, againSnap.properties.id);
            }
            await invalidateScene(sid);
          },
          redo: async () => {
            const sid = sceneIdRef.current;
            if (sid == null) return;
            const liveIds = merges.map((s) => history.resolveId(s.properties.id));
            const again = (await visualizerApi.mergeAreaSigns(sid, liveIds)) as VisualizerSignFeature;
            const againSnap = asSignSnapshot(again);
            if (againSnap && mergedLogicalId != null) {
              history.remapId(mergedLogicalId, againSnap.properties.id);
            }
            await invalidateScene(sid);
          },
        });
        bumpHistory();
      }
      setSelectedIds(result.created.properties?.id != null ? [Number(result.created.properties.id)] : []);
      setMode("select");
      if (sceneId != null) await invalidateScene(sceneId);
      toast({ title: "Площади объединены" });
    },
    onError: (error: Error) =>
      toast({ title: "Слияние не удалось", description: error.message, variant: "destructive" }),
  });

  const differenceSigns = useMutation({
    mutationFn: async () => {
      if (sceneId == null) throw new Error("Сцена не выбрана");
      if (!canDifferenceSelection) {
        throw new Error("Выберите ровно две незаблокированные площади");
      }
      const beforeSnaps = selectedIds
        .map((id) => {
          const feature = signs.find((f) => Number(f.properties?.id) === id);
          return feature ? asSignSnapshot(feature) : null;
        })
        .filter((s): s is VisualizerSignSnapshot => s != null);
      const created = (await visualizerApi.differenceAreaSigns(
        sceneId,
        selectedIds,
      )) as VisualizerSignFeature;
      return { created, beforeSnaps };
    },
    onSuccess: async (result) => {
      if (!historyReplayingRef.current && result.beforeSnaps.length === 2) {
        const history = historyRef.current;
        const pair = result.beforeSnaps;
        const createdSnap = asSignSnapshot(result.created);
        const resultLogicalId = createdSnap?.properties.id;
        history.push({
          undo: async () => {
            const sid = sceneIdRef.current;
            if (sid == null || resultLogicalId == null) return;
            const liveId = history.resolveId(resultLogicalId);
            await visualizerApi.deleteSign(sid, liveId);
            for (const snap of pair) {
              const again = (await visualizerApi.createSign(
                sid,
                featureToCreatePayload(snap),
              )) as VisualizerSignFeature;
              const againSnap = asSignSnapshot(again);
              if (againSnap) history.remapId(snap.properties.id, againSnap.properties.id);
            }
            await invalidateScene(sid);
          },
          redo: async () => {
            const sid = sceneIdRef.current;
            if (sid == null) return;
            const liveIds = pair.map((s) => history.resolveId(s.properties.id));
            const again = (await visualizerApi.differenceAreaSigns(
              sid,
              liveIds,
            )) as VisualizerSignFeature;
            const againSnap = asSignSnapshot(again);
            if (againSnap && resultLogicalId != null) {
              history.remapId(resultLogicalId, againSnap.properties.id);
            }
            await invalidateScene(sid);
          },
        });
        bumpHistory();
      }
      setSelectedIds(
        result.created.properties?.id != null ? [Number(result.created.properties.id)] : [],
      );
      setMode("select");
      if (sceneId != null) await invalidateScene(sceneId);
      toast({ title: "Площади вычтены" });
    },
    onError: (error: Error) =>
      toast({ title: "Вычитание не удалось", description: error.message, variant: "destructive" }),
  });

  const splitSign = useMutation({
    mutationFn: async (coordinates: number[][]) => {
      if (sceneId == null) throw new Error("Сцена не выбрана");
      if (selectedId == null || !canScissorsSelection) {
        throw new Error("Выберите один незаблокированный линейный или площадной знак");
      }
      const feature = signs.find((f) => Number(f.properties?.id) === selectedId);
      const before = feature ? asSignSnapshot(feature) : null;
      if (!before) throw new Error("Не удалось прочитать знак");
      const collection = await visualizerApi.splitSign(sceneId, selectedId, coordinates);
      const created = (collection.features ?? []) as VisualizerSignFeature[];
      if (created.length < 2) throw new Error("Разрез не вернул части");
      return { created, before, coordinates };
    },
    onSuccess: async (result) => {
      if (!historyReplayingRef.current) {
        const history = historyRef.current;
        const sourceLogicalId = result.before.properties.id;
        const partLogicalIds = result.created
          .map((f) => asSignSnapshot(f)?.properties.id)
          .filter((id): id is number => id != null);
        const cutter = result.coordinates;
        history.push({
          undo: async () => {
            const sid = sceneIdRef.current;
            if (sid == null) return;
            for (const partId of partLogicalIds) {
              await visualizerApi.deleteSign(sid, history.resolveId(partId));
            }
            const again = (await visualizerApi.createSign(
              sid,
              featureToCreatePayload(result.before),
            )) as VisualizerSignFeature;
            const againSnap = asSignSnapshot(again);
            if (againSnap) history.remapId(sourceLogicalId, againSnap.properties.id);
            await invalidateScene(sid);
          },
          redo: async () => {
            const sid = sceneIdRef.current;
            if (sid == null) return;
            const liveSource = history.resolveId(sourceLogicalId);
            const collection = await visualizerApi.splitSign(sid, liveSource, cutter);
            const againParts = (collection.features ?? []) as VisualizerSignFeature[];
            againParts.forEach((feat, index) => {
              const snap = asSignSnapshot(feat);
              const logical = partLogicalIds[index];
              if (snap && logical != null) history.remapId(logical, snap.properties.id);
            });
            await invalidateScene(sid);
          },
        });
        bumpHistory();
      }
      const ids = result.created
        .map((f) => Number(f.properties?.id))
        .filter((id) => Number.isFinite(id));
      setSelectedIds(ids);
      setDraft([]);
      setMode("select");
      if (sceneId != null) await invalidateScene(sceneId);
      toast({ title: `Разрезано на частей: ${result.created.length}` });
    },
    onError: (error: Error) =>
      toast({ title: "Разрез не удался", description: error.message, variant: "destructive" }),
  });

  splitSignRef.current = (coordinates) => {
    if (!canManage || splitSign.isPending) return;
    splitSign.mutate(coordinates);
  };

  const upload = useMutation({
    mutationFn: (file: File) => visualizerApi.uploadAsset(file.name.replace(/\.[^.]+$/, ""), file),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["visualizer", "library"] });
    },
    onError: (error: Error) => toast({ title: "Файл не принят", description: error.message }),
  });

  const draftGeoJson = useMemo<GeoJSON.FeatureCollection>(() => {
    if (draft.length < 2) {
      return { type: "FeatureCollection", features: [] };
    }
    if (freehandMode || shapeMode === "line") {
      return {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            properties: {},
            geometry: { type: "LineString", coordinates: draft },
          },
        ],
      };
    }
    if (shapeMode === "polygon" && draft.length >= 3) {
      return {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            properties: {},
            geometry: { type: "Polygon", coordinates: [[...draft, draft[0]]] },
          },
        ],
      };
    }
    return { type: "FeatureCollection", features: [] };
  }, [draft, shapeMode, freehandMode]);

  const finishFreehandRef = useRef<() => void>(() => {});
  finishFreehandRef.current = () => {
    const points = draftRef.current;
    if (points.length < 2) {
      toast({ title: "Слишком короткий штрих" });
      setDraft([]);
      return;
    }
    placeShape.mutate({ kind: "line", coordinates: points });
  };

  const [catalogSearchInput, setCatalogSearchInput] = useState("");
  const [catalogSearchDebounced, setCatalogSearchDebounced] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => setCatalogSearchDebounced(catalogSearchInput), 300);
    return () => window.clearTimeout(timer);
  }, [catalogSearchInput]);

  useEffect(() => {
    if (selected == null) {
      setCatalogSearchInput("");
      setCatalogSearchDebounced("");
    }
  }, [selected]);

  const catalogSearchQ = useMemo(() => {
    const raw = catalogSearchDebounced.trim();
    if (!raw) return undefined;
    if (/^#\d+$/.test(raw)) return raw.slice(1);
    return raw;
  }, [catalogSearchDebounced]);

  const catalogObjectsQuery = useQuery({
    queryKey: ["visualizer", "catalog-objects", catalogSearchQ ?? ""],
    queryFn: () =>
      objectsApi.list({
        limit: 50,
        status: "active",
        ...(catalogSearchQ ? { q: catalogSearchQ } : {}),
      }),
    enabled: selected != null,
    staleTime: 30_000,
  });

  const linkedObjectQuery = useQuery({
    queryKey: ["visualizer", "object", selectedObjectId],
    queryFn: () => objectsApi.get(selectedObjectId as number),
    enabled: selectedObjectId != null,
  });

  const catalogObjectOptions = useMemo((): ComboboxOption[] => {
    const byId = new Map<number, ComboboxOption>();
    for (const obj of catalogObjectsQuery.data ?? []) {
      byId.set(obj.id, {
        value: String(obj.id),
        label: `${obj.name || "Без имени"} (#${obj.id})`,
        shortLabel: obj.name || `#${obj.id}`,
        fullLabel: obj.full_name || obj.name || undefined,
      });
    }
    const linked = linkedObjectQuery.data;
    if (linked && !byId.has(linked.id)) {
      byId.set(linked.id, {
        value: String(linked.id),
        label: `${linked.name || "Без имени"} (#${linked.id})`,
        shortLabel: linked.name || `#${linked.id}`,
        fullLabel: linked.full_name || linked.name || undefined,
      });
    }
    return Array.from(byId.values()).sort((a, b) =>
      a.label.localeCompare(b.label, "ru"),
    );
  }, [catalogObjectsQuery.data, linkedObjectQuery.data]);

  const linkedObjectName =
    linkedObjectQuery.data?.name ||
    catalogObjectOptions.find((o) => o.value === String(selectedObjectId))?.shortLabel ||
    null;

  const signCardQuery = useQuery({
    queryKey: ["visualizer", "sign-card", sceneId, selectedId],
    queryFn: () => visualizerApi.getSignCard(sceneId as number, selectedId as number),
    enabled: sceneId != null && selectedId != null && selectedIds.length === 1,
  });

  const [cardDraft, setCardDraft] = useState<Record<string, string>>({});

  useEffect(() => {
    const values = signCardQuery.data?.values ?? {};
    const next: Record<string, string> = {};
    for (const [key, value] of Object.entries(values)) {
      next[key] = value == null ? "" : String(value);
    }
    for (const section of signCardQuery.data?.sections ?? []) {
      for (const field of section.fields) {
        if (!(field.field_key in next)) {
          next[field.field_key] = field.value == null ? "" : String(field.value);
        }
      }
    }
    setCardDraft(next);
  }, [signCardQuery.data]);

  const saveSignCard = useMutation({
    mutationFn: () => {
      if (sceneId == null || selectedId == null) throw new Error("Знак не выбран");
      const values: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(cardDraft)) {
        values[key] = value;
      }
      return visualizerApi.putSignCard(sceneId, selectedId, values);
    },
    onSuccess: async () => {
      if (sceneId != null && selectedId != null) {
        await queryClient.invalidateQueries({
          queryKey: ["visualizer", "sign-card", sceneId, selectedId],
        });
      }
      toast({ title: "Карточка знака сохранена" });
    },
    onError: (error: Error) =>
      toast({ title: "Карточка знака", description: error.message, variant: "destructive" }),
  });

  const styleForm = selected && selectedIds.length === 1 ? (
    <form
      className="space-y-2 border-t pt-3 text-sm"
      onSubmit={(event) => {
        event.preventDefault();
        patchSign.mutate({
          size_px: sizePx,
          rotation_deg: rotation,
          stroke_color: strokeColor,
          stroke_width: strokeWidth,
          stroke_dash: strokeDash,
          fill_color: fillColor,
          fill_opacity: fillOpacity,
          label,
          object_id: selectedObjectId,
          clear_object: selectedObjectId == null,
        });
      }}
    >
      <p className="font-medium">{selectedIsText ? "Текстовая аннотация" : "Выбранный знак"}</p>
      {selectedIsText ? (
        <>
          <label className="block text-xs">
            Текст
            <Input value={label} onChange={(event) => setLabel(event.target.value)} />
          </label>
          <label className="block text-xs">
            Цвет
            <Input type="color" value={strokeColor} onChange={(event) => setStrokeColor(event.target.value)} />
          </label>
          <label className="block text-xs">
            Размер шрифта
            <Input
              type="number"
              min={10}
              max={72}
              value={sizePx}
              onChange={(event) => setSizePx(Number(event.target.value))}
            />
          </label>
          <label className="block text-xs">
            Поворот
            <Input
              type="number"
              min={-360}
              max={360}
              value={rotation}
              onChange={(event) => setRotation(Number(event.target.value))}
            />
          </label>
        </>
      ) : selected.geometry.type === "Point" ? (
        <>
          <label className="block text-xs">
            Размер
            <Input
              type="number"
              min={8}
              max={256}
              value={sizePx}
              onChange={(event) => setSizePx(Number(event.target.value))}
            />
          </label>
          <label className="block text-xs">
            Поворот
            <Input
              type="number"
              min={-360}
              max={360}
              value={rotation}
              onChange={(event) => setRotation(Number(event.target.value))}
            />
          </label>
          <label className="block text-xs">
            Подпись
            <Input value={label} onChange={(event) => setLabel(event.target.value)} />
          </label>
        </>
      ) : (
        <>
          <label className="block text-xs">
            Цвет обводки
            <Input type="color" value={strokeColor} onChange={(event) => setStrokeColor(event.target.value)} />
          </label>
          <label className="block text-xs">
            Толщина
            <Input
              type="number"
              min={1}
              max={24}
              value={strokeWidth}
              onChange={(event) => setStrokeWidth(Number(event.target.value))}
            />
          </label>
          <label className="block text-xs">
            Штрих
            <select
              className="mt-0.5 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
              value={strokeDash}
              onChange={(event) =>
                setStrokeDash(event.target.value as "solid" | "dash" | "dot" | "dash_dot")
              }
              aria-label="Штрих обводки"
            >
              <option value="solid">Сплошная</option>
              <option value="dash">Штрих</option>
              <option value="dot">Точка</option>
              <option value="dash_dot">Штрих-точка</option>
            </select>
          </label>
          {selected.geometry.type === "Polygon" && (
            <>
              <label className="block text-xs">
                Цвет заливки
                <Input
                  type="color"
                  value={fillColor}
                  onChange={(event) => setFillColor(event.target.value)}
                />
              </label>
              <label className="block text-xs">
                Прозрачность заливки
                <Input
                  type="number"
                  min={0}
                  max={1}
                  step={0.1}
                  value={fillOpacity}
                  onChange={(event) => setFillOpacity(Number(event.target.value))}
                />
              </label>
            </>
          )}
          <label className="block text-xs">
            Подпись
            <Input value={label} onChange={(event) => setLabel(event.target.value)} />
          </label>
        </>
      )}
      <div className="space-y-1">
        <div className="flex items-center gap-1 text-xs">
          <span>Объект каталога</span>
          <SectionHelpTooltip label="Справка: привязка к объекту каталога">
            <p>Необязательная связь знака с объектом ОИ (только ссылка, геометрия каталога не меняется).</p>
            <p>
              Поиск в списке идёт на сервере по имени или числовому id (можно с «#»). «Без привязки»
              снимает связь.
            </p>
            <p>
              «Указать на карте» — клик по объекту подложки ОИ сразу сохраняет привязку (можно отменить
              через undo). Esc или повторный клик по кнопке — выход из режима без смены связи.
            </p>
            <p>Сохранение стиля также записывает выбранную в списке связь на знак сцены.</p>
          </SectionHelpTooltip>
        </div>
        <div className="flex items-center gap-1">
          <Combobox
            options={catalogObjectOptions}
            value={selectedObjectId != null ? String(selectedObjectId) : undefined}
            onValueChange={(value) => {
              if (!value) {
                setSelectedObjectId(null);
                return;
              }
              const id = Number(value);
              setSelectedObjectId(Number.isFinite(id) ? id : null);
            }}
            placeholder="Не привязан"
            searchPlaceholder="Поиск по имени или #id…"
            emptyText={
              catalogObjectsQuery.isFetching ? "Поиск…" : "Объекты не найдены"
            }
            clearText="Без привязки"
            allowClear
            filterMode="server"
            onSearchChange={setCatalogSearchInput}
            disabled={!canManage || bindObjectFromMap}
            className="h-9 flex-1"
          />
          {canManage && (
            <Button
              type="button"
              size="icon"
              variant={bindObjectFromMap ? "default" : "outline"}
              className="h-9 w-9 shrink-0"
              title={
                bindObjectFromMap
                  ? "Отменить указание на карте (Esc)"
                  : "Указать объект на карте (подложка ОИ)"
              }
              aria-label={
                bindObjectFromMap ? "Отменить указание на карте" : "Указать объект на карте"
              }
              aria-pressed={bindObjectFromMap}
              disabled={bindObjectBusy}
              onClick={() => {
                if (bindObjectFromMap) {
                  setBindObjectFromMap(false);
                  return;
                }
                setToolMode("select");
                setDraft([]);
                setTextDraft(null);
                setBindObjectFromMap(true);
              }}
            >
              <Crosshair className="h-4 w-4" />
            </Button>
          )}
        </div>
        {bindObjectFromMap && (
          <p className="text-xs text-muted-foreground">Кликните объект на карте · Esc — отмена</p>
        )}
        {selectedObjectId != null && linkedObjectName && (
          <p className="text-xs text-muted-foreground">
            {linkedObjectName}.{" "}
            <a className="underline" href={`/oi?objectId=${selectedObjectId}`}>
              Открыть в каталоге
            </a>
          </p>
        )}
        {catalogObjectsQuery.isLoading && (
          <p className="text-xs text-muted-foreground">Загрузка объектов…</p>
        )}
      </div>
      {canManage && !isSignEffectivelyLocked(selectedId) && (
        <Button type="submit" size="sm" className="w-full">
          Сохранить стиль
        </Button>
      )}
      {canManage && isSignEffectivelyLocked(selectedId) && (
        <p className="text-xs text-amber-800 dark:text-amber-200">Знак заблокирован — стиль не меняется</p>
      )}
    </form>
  ) : null;

  const signCardForm =
    selectedId != null && selectedIds.length === 1 ? (
      <div className="space-y-2 border-t pt-3 text-sm">
        <div className="flex items-center gap-1">
          <p className="font-medium">Карточка знака</p>
          <SectionHelpTooltip label="Справка: карточка знака">
            <p>Атрибуты по опубликованному шаблону (scope = KLS знака или универсальный).</p>
            <p>Привязки к объектам каталога здесь не задаются — только поля шаблона.</p>
            <p>Esc снимает выбор знака на карте и в списке.</p>
          </SectionHelpTooltip>
        </div>
        {signCardQuery.isLoading ? (
          <p className="text-xs text-muted-foreground">Загрузка…</p>
        ) : signCardQuery.data?.message && !signCardQuery.data.template_id ? (
          <p className="text-xs text-muted-foreground">{signCardQuery.data.message}</p>
        ) : (
          <form
            className="space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (canManage) saveSignCard.mutate();
            }}
          >
            {(signCardQuery.data?.sections ?? []).map((section) => (
              <div key={section.code} className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">{section.name}</p>
                {section.fields.map((field) => (
                  <label key={field.field_key} className="block text-xs">
                    {field.label}
                    {field.is_required ? " *" : ""}
                    <Input
                      value={cardDraft[field.field_key] ?? ""}
                      disabled={!canManage || field.is_readonly}
                      onChange={(event) =>
                        setCardDraft((prev) => ({
                          ...prev,
                          [field.field_key]: event.target.value,
                        }))
                      }
                    />
                  </label>
                ))}
              </div>
            ))}
            {canManage && (signCardQuery.data?.template_id != null) && (
              <Button type="submit" size="sm" className="w-full" disabled={saveSignCard.isPending}>
                Сохранить карточку
              </Button>
            )}
          </form>
        )}
      </div>
    ) : null;

  return (
    <div className="flex h-screen flex-col bg-background">
      <Header />
      <div className="flex min-h-0 flex-1">
        <div className="relative min-h-0 min-w-0 flex-1">
          <ModuleMapUiStubProvider setActiveTool={() => {}}>
            <Suspense fallback={<div className="p-4 text-sm text-muted-foreground">Загрузка карты…</div>}>
              <MapLibreMap
                selectedObject={null}
                geometries={[]}
                activeTool="select"
                isDrawing={false}
                onDrawingComplete={() => {}}
                useMvt
                catalogUnderlayEnabled
                drawingSessionEnabled={false}
                cadastralLayersVisible={false}
                telegramIncidentsVisible={false}
                workItemsVisible={false}
                showBaseLayerSwitcher
                baseLayerSwitcherVariant="style-card"
                showMapExportButton
                symbolGeoJson={mapSignsGeoJson}
                selectedSymbolId={selectedId}
                selectedSymbolIds={selectedIds}
                symbolPlaceMode={placeMode}
                symbolPanMode={panMode || measureMode}
                symbolTextMode={textMode}
                symbolFreehandMode={freehandMode}
                symbolEraserMode={eraserMode}
                symbolLassoMode={lassoMode}
                symbolRotateMode={rotateMode}
                symbolVertexMode={vertexMode}
                symbolShapeMode={shapeMode}
                symbolBindObjectFromMapMode={bindObjectFromMap}
                symbolDraft={draftGeoJson}
                onObjectSelect={(objectId) => bindObjectFromMapClickRef.current(objectId)}
                symbolSnapLngLat={snapLngLat}
                onSymbolPlace={(lng, lat) => {
                  if (!canManage) return;
                  placeSign.mutate({ lng, lat });
                }}
                onSymbolTextPlace={(lng, lat) => {
                  if (!canManage || sceneId == null) return;
                  setTextDraft({ lng, lat });
                  setTextDraftLabel("");
                }}
                onSymbolFreehandPoint={(lng, lat) => {
                  if (!canManage) return;
                  setDraft((points) => [...points, [lng, lat]]);
                }}
                onSymbolFreehandFinish={() => finishFreehandRef.current()}
                onSymbolVertex={(lng, lat) => {
                  if (!canManage) return;
                  setDraft((points) => [...points, [lng, lat]]);
                }}
                onSymbolFinish={() => finishDraftRef.current()}
                onSymbolSelect={(id) => {
                  setBindObjectFromMap(false);
                  setToolMode("select");
                  setDraft([]);
                  setTextDraft(null);
                  setSelectedIds((current) =>
                    current.length === 1 && current[0] === id ? [] : [id],
                  );
                }}
                onSymbolErase={(id) => {
                  if (!canManage) return;
                  if (isSignEffectivelyLocked(id)) {
                    toast({ title: "Знак заблокирован" });
                    return;
                  }
                  removeSign.mutate(id);
                }}
                onSymbolLassoComplete={(ring) => {
                  const ids = signsInsideLasso(signs, ring);
                  setSelectedIds(ids);
                  setToolMode("select");
                  if (ids.length === 0) {
                    toast({ title: "В области нет знаков" });
                  }
                }}
                onSymbolRotateCommit={(id, payload) => {
                  if (!canManage || sceneId == null) return;
                  if (isSignEffectivelyLocked(id)) {
                    toast({ title: "Знак заблокирован" });
                    return;
                  }
                  const feature = signs.find((f) => Number(f.properties?.id) === id);
                  const beforeSnap = feature ? asSignSnapshot(feature) : null;
                  const beforePatch = beforeSnap ? featureToStylePatch(beforeSnap) : null;
                  const afterPatch: Record<string, unknown> =
                    payload.rotationDeg != null
                      ? { rotation_deg: payload.rotationDeg }
                      : payload.geometry
                        ? {
                            coordinates:
                              payload.geometry.type === "Polygon"
                                ? payload.geometry.coordinates[0]
                                : payload.geometry.coordinates,
                          }
                        : {};
                  if (Object.keys(afterPatch).length === 0) return;
                  void visualizerApi.updateSign(sceneId, id, afterPatch).then(() => {
                    if (beforePatch) pushPatchHistory(id, beforePatch, afterPatch);
                    return invalidateScene(sceneId);
                  });
                }}
                onSymbolDrag={(id, lng, lat) => {
                  if (!canManage || sceneId == null) return;
                  if (isSignEffectivelyLocked(id)) {
                    toast({ title: "Знак заблокирован" });
                    return;
                  }
                  setSelectedId(id);
                  const feature = signs.find((f) => Number(f.properties?.id) === id);
                  const beforeSnap = feature ? asSignSnapshot(feature) : null;
                  const beforePatch = beforeSnap ? featureToStylePatch(beforeSnap) : null;
                  const afterPatch = { lng, lat };
                  void visualizerApi.updateSign(sceneId, id, afterPatch).then(() => {
                    if (beforePatch) pushPatchHistory(id, beforePatch, afterPatch);
                    return invalidateScene(sceneId);
                  });
                }}
                onSymbolGeometryChange={(id, geometry) => {
                  if (!canManage || sceneId == null) return;
                  if (isSignEffectivelyLocked(id)) {
                    toast({ title: "Знак заблокирован" });
                    return;
                  }
                  const coordinates =
                    geometry.type === "Polygon" ? geometry.coordinates[0] : geometry.coordinates;
                  setSelectedId(id);
                  const feature = signs.find((f) => Number(f.properties?.id) === id);
                  const beforeSnap = feature ? asSignSnapshot(feature) : null;
                  const beforePatch = beforeSnap ? featureToStylePatch(beforeSnap) : null;
                  const afterPatch = { coordinates };
                  void visualizerApi.updateSign(sceneId, id, afterPatch).then(() => {
                    if (beforePatch) pushPatchHistory(id, beforePatch, afterPatch);
                    return invalidateScene(sceneId);
                  });
                }}
                onClearObjectSelection={() => {
                  if (bindObjectFromMapRef.current) {
                    setBindObjectFromMap(false);
                    return;
                  }
                  setSelectedId(null);
                }}
                onMapInstance={setMapInstance}
              />
            </Suspense>
          </ModuleMapUiStubProvider>

          {bindObjectFromMap && (
            <div
              className={cn(
                "pointer-events-none absolute left-1/2 top-3 max-w-[min(480px,calc(100vw-2rem))] -translate-x-1/2 rounded-md border border-border/80 bg-background/95 px-3 py-2 text-center text-xs shadow-md backdrop-blur-sm md:text-sm",
                MAP_PANEL_Z.drawingBanner,
              )}
              role="status"
              aria-live="polite"
            >
              <div className="font-medium text-foreground">Привязка к объекту каталога</div>
              <div className="mt-1 text-muted-foreground">
                Кликните объект на подложке ОИ. Esc — отмена без смены связи.
              </div>
            </div>
          )}

          <VisualizerMapCompass map={mapInstance} />

          <VisualizerToolbar
            mode={toolMode}
            canManage={canManage}
            canDraw={sceneId != null && pick != null}
            canPlaceText={sceneId != null}
            canDelete={
              selectedIds.length > 0 &&
              selectedIds.some((id) => !isSignEffectivelyLocked(id))
            }
            canCopy={
              selectedIds.length > 0 &&
              selectedIds.some((id) => !isSignEffectivelyLocked(id))
            }
            canMerge={canManage && canMergeSelection}
            canDifference={canManage && canDifferenceSelection}
            canVertexEdit={canManage && canVertexEditSelection}
            canScissors={canManage && canScissorsSelection}
            canUndo={canUndo}
            canRedo={canRedo}
            historyBusy={historyBusy || patchSign.isPending || removeSign.isPending}
            copyBusy={copySign.isPending}
            mergeBusy={mergeSigns.isPending}
            differenceBusy={differenceSigns.isPending}
            onUndo={() => void runHistoryRef.current("undo")}
            onRedo={() => void runHistoryRef.current("redo")}
            onCopy={() => copySignRef.current()}
            onMerge={() => mergeSigns.mutate()}
            onDifference={() => differenceSigns.mutate()}
            draftVertexCount={draft.length}
            map={mapInstance}
            gridEnabled={snapGridPrefs.grid}
            snapEnabled={snapGridPrefs.snap}
            gridStepMeters={snapGridPrefs.stepMeters}
            onGridEnabledChange={(grid) =>
              setSnapGridPrefsPersist({ ...snapGridPrefs, grid })
            }
            onSnapEnabledChange={(snap) =>
              setSnapGridPrefsPersist({ ...snapGridPrefs, snap })
            }
            onGridStepMetersChange={(stepMeters: VisualizerGridStepMeters) =>
              setSnapGridPrefsPersist({ ...snapGridPrefs, stepMeters })
            }
            styleStrip={{
              show: Boolean(
                selectedIds.length === 1 &&
                  selected &&
                  (selectedIsText ||
                    selected.geometry.type === "LineString" ||
                    selected.geometry.type === "Polygon"),
              ),
              variant: selectedIsText ? "text" : "stroke",
              canManage,
              strokeColor,
              fillColor,
              strokeWidth,
              strokeDash,
              fillOpacity,
              showFill: selected?.geometry.type === "Polygon" && !selectedIsText,
              fontSize: sizePx,
              onStrokeColorChange: setStrokeColor,
              onFillColorChange: setFillColor,
              onStrokeWidthChange: setStrokeWidth,
              onStrokeDashChange: setStrokeDash,
              onFillOpacityChange: setFillOpacity,
              onFontSizeChange: setSizePx,
              saving: patchSign.isPending,
              onSaveStyle: () => {
                if (selectedIsText) {
                  patchSign.mutate({
                    stroke_color: strokeColor,
                    size_px: sizePx,
                    label,
                  });
                  return;
                }
                patchSign.mutate({
                  stroke_color: strokeColor,
                  stroke_width: strokeWidth,
                  stroke_dash: strokeDash,
                  fill_color: fillColor,
                  fill_opacity: fillOpacity,
                });
              },
            }}
            onModeChange={setMode}
            onDelete={() => {
              const unlocked = selectedIds.filter((id) => !isSignEffectivelyLocked(id));
              if (unlocked.length === 0) return;
              unlocked.forEach((id) => removeSign.mutate(id));
            }}
          />

          <Dialog
            open={textDraft != null}
            onOpenChange={(open) => {
              if (!open) {
                setTextDraft(null);
                setTextDraftLabel("");
              }
            }}
          >
            <DialogContent className="sm:max-w-sm">
              <DialogHeader>
                <DialogTitle>Текст на карте</DialogTitle>
              </DialogHeader>
              <Input
                autoFocus
                value={textDraftLabel}
                onChange={(event) => setTextDraftLabel(event.target.value)}
                placeholder="Введите текст"
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    const cleaned = textDraftLabel.trim();
                    if (!textDraft || !cleaned) {
                      toast({ title: "Введите непустой текст", variant: "destructive" });
                      return;
                    }
                    placeText.mutate({ ...textDraft, label: cleaned });
                  }
                }}
              />
              <DialogFooter className="gap-2 sm:gap-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setTextDraft(null);
                    setTextDraftLabel("");
                  }}
                >
                  Отмена
                </Button>
                <Button
                  type="button"
                  disabled={placeText.isPending}
                  onClick={() => {
                    const cleaned = textDraftLabel.trim();
                    if (!textDraft || !cleaned) {
                      toast({ title: "Введите непустой текст", variant: "destructive" });
                      return;
                    }
                    placeText.mutate({ ...textDraft, label: cleaned });
                  }}
                >
                  Сохранить
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <VisualizerLegendToggle
            open={legendOpen}
            onToggle={() => setLegendOpen((value) => !value)}
            entries={legendQuery.data ?? []}
          />
        </div>

        <VisualizerLayersPanel
          collapsed={layersCollapsed}
          onCollapsedChange={setLayersCollapsedPersist}
          widthPx={layersWidthPx}
          onWidthChange={setLayersWidthPersist}
          canManage={canManage}
          scenes={scenesQuery.data ?? []}
          sceneId={sceneId}
          sceneName={sceneName}
          onSceneNameChange={setSceneName}
          onSelectScene={(id) => {
            setSceneId(id);
            setSelectedIds([]);
            setMode("select");
          }}
          onCreateScene={() => createScene.mutate()}
          onDeleteScene={() => {
            if (sceneId != null) removeScene.mutate(sceneId);
          }}
          onExportScene={() => exportScene.mutate()}
          onImportScene={(file) => importScene.mutate(file)}
          onDuplicateScene={() => duplicateScene.mutate()}
          exportBusy={exportScene.isPending}
          importBusy={importScene.isPending}
          duplicateBusy={duplicateScene.isPending}
          signs={signs}
          layerGroups={layerGroups}
          selectedIds={selectedIds}
          onSelectSign={(id, opts) => {
            setToolMode("select");
            setDraft([]);
            if (opts?.shiftKey) {
              setSelectedIds((current) =>
                current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
              );
              return;
            }
            setSelectedIds((current) =>
              current.length === 1 && current[0] === id ? [] : [id],
            );
          }}
          onDeleteSign={(id) => removeSign.mutate(id)}
          onCreateLayerGroup={(name) => createLayerGroup.mutate(name)}
          onRenameLayerGroup={(groupId, name) =>
            patchLayerGroup.mutate({ groupId, data: { name } })
          }
          onDeleteLayerGroup={(groupId) => deleteLayerGroup.mutate(groupId)}
          onToggleGroupLock={(groupId, locked) =>
            patchLayerGroup.mutate({ groupId, data: { is_locked: locked } })
          }
          onToggleGroupVisible={(groupId, visible) =>
            patchLayerGroup.mutate({ groupId, data: { is_visible: visible } })
          }
          onToggleSignLock={(signId, locked) => {
            void patchSignMeta(signId, { is_locked: locked }).catch((error: unknown) =>
              toast({
                title: "Блокировка",
                description: error instanceof Error ? error.message : undefined,
                variant: "destructive",
              }),
            );
          }}
          onToggleSignVisible={(signId, visible) => {
            void patchSignMeta(signId, { is_visible: visible }).catch((error: unknown) =>
              toast({
                title: "Видимость",
                description: error instanceof Error ? error.message : undefined,
                variant: "destructive",
              }),
            );
          }}
          onAssignSignGroup={(signId, groupId) => {
            const afterPatch =
              groupId == null
                ? { clear_layer_group: true }
                : { layer_group_id: groupId };
            void patchSignMeta(signId, afterPatch).catch((error: unknown) =>
              toast({
                title: "Группа знака",
                description: error instanceof Error ? error.message : undefined,
                variant: "destructive",
              }),
            );
          }}
          library={libraryQuery.data}
          pick={pick}
          onPick={setPick}
          onUpload={(file) => upload.mutate(file)}
          styleForm={styleForm}
          signCardForm={signCardForm}
        />
      </div>
    </div>
  );
}
